import React from 'react';

import { PopoverProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useForm } from 'react-final-form';

import { DateFormatPatterns } from '@clearverify/constants';

import { getFieldError, getFieldWarning, hasFieldError } from 'src/utils/finalForm';

import type { DateFieldProps } from './interface';

const POPOVER_PROPS: Partial<PopoverProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
};

const DEFAULT_INVALID_DATE_ERROR_MESSAGE = 'Invalid date format';

export const DateField: React.FC<DateFieldProps> = ({
  input: { name, value, onChange, onBlur },
  meta,
  inputFormat = DateFormatPatterns.shortDateWithSlash,
  fullWidth = false,
  required = false,
  disabled = false,
  warning,
  showErrorOnlyTouched = true,
  showErrorWhenDisabled = true,
  ...props
}) => {
  const { blur } = useForm();
  const [isValidDate, toggleIsValidDate] = React.useState<boolean>(true);

  const handleChange = React.useCallback(
    (date: DateTime | null) => {
      const formattedDate = date ? date.toISO() : date;

      onChange(formattedDate);
      toggleIsValidDate(Boolean(date?.isValid));
      blur(name);
    },
    [name, blur, onChange],
  );

  const showError = showErrorWhenDisabled || !disabled;
  const hasError = (hasFieldError(meta) || (!isValidDate && !props.mayHaveEmptyValue)) && showError;

  const getHelperText = () => {
    if (!showError) return '';

    const fieldError =
      getFieldError(meta, showErrorOnlyTouched) || getFieldWarning(value, meta, warning, disabled);

    if (fieldError) {
      return fieldError;
    }

    if (!fieldError && !isValidDate && !props.mayHaveEmptyValue) {
      return DEFAULT_INVALID_DATE_ERROR_MESSAGE;
    }

    return '';
  };

  const textFieldProps = {
    error: hasError,
    helperText: showError ? getHelperText() : '',
    fullWidth,
    required,
    disabled,
    onBlur,
  };

  const datePickerProps = {
    name,
    format: inputFormat,
    disabled,
    value: value ? DateTime.fromISO(value) : null,
    onChange: handleChange,
    slotProps: { textField: textFieldProps, popover: POPOVER_PROPS },
    ...props,
  };

  return <DatePicker {...datePickerProps} />;
};
